import React, { Component, ReactNode } from "react";
import { Stack } from "@mui/material";

interface ErrorBoundaryState {
  hasError: boolean;
  errorMessage: string;
  errorStack: string;
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
    errorMessage: "Oops! Something went wrong.", // Default message
    errorStack: "", // Keep the stack trace but won't show it to the user
  };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state to show error message in the UI
    return {
      hasError: true,
      errorMessage: error.message,
      errorStack: error.stack || "",
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error to an error tracking service (e.g., Sentry, LogRocket, etc.)

    console.error("Error caught by ErrorBoundary:", error, errorInfo);

    // Optionally: You can provide custom error handling or fallbacks here if needed
    if (!error.message) {
      this.setState({
        errorMessage: "Something unexpected happened. Please try again later.",
      });
    }
  }

  handleReload = () => {
    window.location.reload();
  }

  render() {
    if (this.state.hasError) {
      return (
        <Stack height={"80vh"} justifyContent={"center"} alignItems={"center"}>
          <div className="p-20 text-align-center">
            <h2>Oops! Something went wrong.</h2>
            <p>{this.state.errorMessage}</p>
          </div>
        </Stack>
      );
    }

    // If no error, render child components
    return this.props.children;
  }
}

export default ErrorBoundary;
